import * as React from 'react'
import type { HoneybookConnectionDto } from '@lib/dto/honeybook.dto'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Facts } from '@/components/ui/facts'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { requestErrorMessage } from '@/helpers/apiClient'
import {
  useDisconnectHoneybook,
  useForgetHoneybook,
  useMatchHoneybookAgain,
  useRefreshHoneybook,
  useSaveHoneybookSettings,
  useStartHoneybookSignIn,
  useVenueHoneybook,
} from '@/hooks/useHoneyBook'
import { instantLabel } from '@/lib/instantLabel'
import { HoneyBookMeanings } from './HoneyBookMeanings'
import { HoneyBookEventLists } from './HoneyBookEventLists'
import { HoneyBookReviewList } from './HoneyBookReviewList'
import { HoneyBookSwitch } from './HoneyBookSwitch'
import { Field } from '@/components/ui/field'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'

const STATE: Record<HoneybookConnectionDto['state'], { label: string; tone: BadgeTone }> = {
  first_read: { label: 'Reading for the first time', tone: 'info' },
  working: { label: 'Working', tone: 'success' },
  not_working: { label: 'Not working', tone: 'danger' },
  disconnected: { label: 'Disconnected', tone: 'neutral' },
  switched: { label: 'Switched to FLOH Venues', tone: 'neutral' },
}

/** Sends the person to HoneyBook's own sign-in; they come back to the return page. */
function ConnectButton({ venueId, label }: { venueId: string; label: string }) {
  const start = useStartHoneybookSignIn(venueId)
  return (
    <div className="space-y-2">
      <Button
        type="button"
        disabled={start.isPending}
        onClick={() => start.mutate(undefined, { onSuccess: ({ url }) => window.location.assign(url) })}
      >
        {start.isPending ? 'Opening HoneyBook…' : label}
      </Button>
      {start.isError ? <FormError>{requestErrorMessage(start.error)}</FormError> : null}
    </div>
  )
}

function NotConnected({ venueId }: { venueId: string }) {
  return (
    <div className="space-y-4">
      <p className="m-0 text-sm text-ink-muted">
        Connect with a HoneyBook admin or owner: anyone else can’t see every lead. FLOH Venues only reads HoneyBook; it never changes
        anything there.
      </p>
      <ConnectButton venueId={venueId} label="Connect HoneyBook" />
    </div>
  )
}

/** A connection that was disconnected: connect it again, or forget it to connect another company. */
function Disconnected({ venueId }: { venueId: string }) {
  const forget = useForgetHoneybook(venueId)
  const [confirming, setConfirming] = React.useState(false)
  return (
    <div className="flex flex-wrap items-start gap-3">
      <ConnectButton venueId={venueId} label="Connect HoneyBook again" />
      <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
        Forget this connection
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Forget the old HoneyBook connection?"
        description="Do this to connect a different HoneyBook company. The events it made become the venue’s own, and its leads stop counting. Connecting the same company again doesn’t need it."
        confirmLabel="Forget it"
        destructive
        busy={forget.isPending}
        error={forget.isError ? requestErrorMessage(forget.error) : null}
        onConfirm={() => forget.mutate(undefined, { onSuccess: () => setConfirming(false) })}
      />
    </div>
  )
}

/** Read the venue's HoneyBook now rather than at the next hour. */
function RefreshButton({ venueId, reading }: { venueId: string; reading: boolean }) {
  const refresh = useRefreshHoneybook(venueId)
  return (
    <div className="space-y-2">
      <Button type="button" variant="outline" disabled={reading || refresh.isPending} onClick={() => refresh.mutate()}>
        {reading || refresh.isPending ? 'Reading…' : 'Refresh'}
      </Button>
      {refresh.isError ? <FormError>{requestErrorMessage(refresh.error)}</FormError> : null}
    </div>
  )
}

/** Match the venue's own events to HoneyBook's bookings again, with a full read. */
function MatchAgainButton({ venueId, reading }: { venueId: string; reading: boolean }) {
  const match = useMatchHoneybookAgain(venueId)
  const [confirming, setConfirming] = React.useState(false)
  return (
    <div>
      <Button type="button" variant="outline" disabled={reading} onClick={() => setConfirming(true)}>
        Match events again
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Match events to HoneyBook again?"
        description="HoneyBook is read in full, and the venue’s own booked events are checked against its bookings. An event that is clearly a booking follows it from then on; the rest wait here for you, including ones you said matched none before."
        confirmLabel="Match events"
        busy={match.isPending}
        error={match.isError ? requestErrorMessage(match.error) : null}
        onConfirm={() => match.mutate(undefined, { onSuccess: () => setConfirming(false) })}
      />
    </div>
  )
}

function Connected({ venueId }: { venueId: string }) {
  const disconnect = useDisconnectHoneybook(venueId)
  const [confirming, setConfirming] = React.useState(false)
  return (
    <div>
      <Button type="button" variant="outline" onClick={() => setConfirming(true)}>
        Disconnect
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Disconnect HoneyBook?"
        description="FLOH Venues stops reading this venue’s HoneyBook. Connecting the same company again picks up where it left off."
        confirmLabel="Disconnect"
        destructive
        busy={disconnect.isPending}
        error={disconnect.isError ? requestErrorMessage(disconnect.error) : null}
        onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setConfirming(false) })}
      />
    </div>
  )
}

function ConnectionActions({ venueId, connection }: { venueId: string; connection: HoneybookConnectionDto }) {
  // One way: a switched venue's connection is never read, connected or disconnected again.
  if (connection.state === 'switched') return null
  if (connection.state === 'disconnected') return <Disconnected venueId={venueId} />
  if (connection.state === 'not_working') {
    return (
      <div className="space-y-4">
        <Callout tone="danger" title="HoneyBook stopped accepting this connection">
          Connect again with the same HoneyBook company to pick up where it left off.
        </Callout>
        <div className="flex flex-wrap items-start gap-3">
          <ConnectButton venueId={venueId} label="Connect again" />
          <Connected venueId={venueId} />
        </div>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-start gap-3">
      <RefreshButton venueId={venueId} reading={connection.reading} />
      <MatchAgainButton venueId={venueId} reading={connection.reading} />
      <Connected venueId={venueId} />
    </div>
  )
}

/** When HoneyBook was last read, or that it is being read now. */
function lastRead(connection: HoneybookConnectionDto): string | null {
  if (connection.reading) return 'Reading now…'
  return connection.readAt === null ? null : instantLabel(connection.readAt)
}

/** The track events made from HoneyBook bookings start on. */
function TrackField({ venueId, connection, tracks }: { venueId: string; connection: HoneybookConnectionDto; tracks: readonly { id: string; name: string }[] }) {
  const save = useSaveHoneybookSettings(venueId)
  return (
    <Field label="New events start on" htmlFor="honeybook-track" hint="Events made from bookings in HoneyBook start on this track. Move any of them after.">
      <Select
        id="honeybook-track"
        value={connection.eventTrackId ?? ''}
        disabled={save.isPending}
        onChange={(e) => save.mutate({ eventTrackId: e.target.value === '' ? null : e.target.value })}
      >
        <option value="">The whole venue</option>
        {tracks.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </Select>
    </Field>
  )
}

function ConnectionView({ venueId, connection, tracks }: { venueId: string; connection: HoneybookConnectionDto; tracks: readonly { id: string; name: string }[] }) {
  const state = STATE[connection.state]
  const switched = connection.state === 'switched'
  const connected = `${instantLabel(connection.connectedAt)}${connection.connectedByName === null ? '' : ` by ${connection.connectedByName}`}`
  return (
    <div className="space-y-4">
      <Facts
        layout="stack"
        facts={[
          { label: 'Company', value: connection.companyName, empty: 'Not named' },
          { label: 'Connected', value: connected, empty: 'Not yet' },
          { label: 'Last read', value: lastRead(connection), empty: 'Not yet' },
          { label: 'Status', value: <Badge tone={state.tone}>{state.label}</Badge>, empty: 'Unknown' },
        ]}
      />
      {connection.state !== 'disconnected' && connection.state !== 'switched' && connection.missing.length > 0 ? (
        <Callout
          tone="attention"
          title="HoneyBook wasn’t given every permission"
          action={connection.state === 'working' ? <ConnectButton venueId={venueId} label="Connect again" /> : undefined}
        >
          <ul className="m-0 list-disc pl-5">
            {connection.missing.map((m) => (
              <li key={m.permission}>{m.effect}</li>
            ))}
          </ul>
          Connect again and allow everything HoneyBook asks about.
        </Callout>
      ) : null}
      <ConnectionActions venueId={venueId} connection={connection} />
      {switched ? null : (
        <>
          {tracks.length > 0 ? <TrackField venueId={venueId} connection={connection} tracks={tracks} /> : null}
          <HoneyBookReviewList venueId={venueId} readAt={connection.readAt} />
          <HoneyBookEventLists connection={connection} />
        </>
      )}
      <HoneyBookSwitch venueId={venueId} connection={connection} />
      <div className="border-t border-line pt-6">
        <HoneyBookMeanings key={connection.readAt ?? 'unread'} venueId={venueId} connection={connection} />
      </div>
    </div>
  )
}

/**
 * The HoneyBook section of a venue's settings (docs/honeybook.spec.md,
 * "Connecting"): which HoneyBook company the venue runs its clients in, and
 * connecting, disconnecting or forgetting it. For whoever sets up integrations,
 * in a company with the HoneyBook feature.
 */
export function VenueHoneyBookSection({ venueId, tracks = [] }: { venueId: string; tracks?: readonly { id: string; name: string }[] }) {
  const honeybook = useVenueHoneybook(venueId)

  if (honeybook.isPending) return <DetailSkeleton />
  if (honeybook.isError) return <ErrorState message={requestErrorMessage(honeybook.error)} onRetry={() => void honeybook.refetch()} />
  const { available, connection } = honeybook.data
  if (!available && connection === null) {
    return (
      <Callout tone="attention" title="HoneyBook can’t be connected yet">
        CSE is still setting up HoneyBook for FLOH Venues.
      </Callout>
    )
  }
  if (connection === null) return <NotConnected venueId={venueId} />
  return <ConnectionView venueId={venueId} connection={connection} tracks={tracks} />
}
